import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import type { ChatSessionSummary } from "../../api/getChatSessions";
import { getChatSessions } from "../../api/getChatSessions";
import { SessionItem } from "./SessionItem";

interface SidebarProps {
  isDarkMode: boolean;
  isSidebarOpen: boolean;
  username: string;
  selectedSessionId: string | null;
  setIsSidebarOpen: (open: boolean) => void;
  setSelectedSessionId: (id: string) => void;
  onCreateNewSession: () => void;
}

function Sidebar({
  isDarkMode,
  isSidebarOpen,
  username,
  selectedSessionId,
  setIsSidebarOpen,
  setSelectedSessionId,
  onCreateNewSession,
}: SidebarProps) {
  // Fetch chat sessions
  const { data: chatSessionsData } = useQuery({
    queryKey: ["chatSessions", username],
    queryFn: () => getChatSessions(username),
    enabled: !!username,
    refetchOnWindowFocus: false,
  });

  const chatSessions: ChatSessionSummary[] = chatSessionsData?.sessions || [];

  // Handle session selection and close sidebar (only on mobile)
  const handleSessionSelect = (id: string) => {
    setSelectedSessionId(id);
    // Only close sidebar on mobile (screen width < 768px, which is the md breakpoint)
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <aside
      className={[
        "border-r",
        "overflow-hidden",
        "transition-all",
        "duration-300",
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-100",
        isSidebarOpen
          ? [
              "fixed",
              "inset-x-0",
              "top-[var(--navbar-height)]",
              "bottom-0",
              "w-full",
              "md:relative",
              "md:w-[280px]",
              "md:inset-x-auto",
              "md:inset-y-auto",
              "md:top-auto",
              "md:bottom-auto",
              "z-50",
            ].join(" ")
          : "w-0",
      ].join(" ")}
    >
      <div
        className={[
          "h-full",
          "w-full",
          "grid",
          "grid-rows-[auto_1fr_auto]",
          "min-h-0",
          isSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        ].join(" ")}
      >
        {/* Sidebar Header */}
        <div
          className={[
            "p-2",
            "md:p-4",
            "border-b",
            "grid",
            "grid-cols-[1fr_auto]",
            "items-center",
            "gap-2",
            "min-w-0",
            isDarkMode ? "border-gray-700" : "border-gray-200",
          ].join(" ")}
        >
          <h4
            className={[
              "text-base",
              "md:text-lg",
              "font-semibold",
              "truncate",
              isDarkMode ? "text-gray-100!" : "text-gray-900!",
            ].join(" ")}
          >
            Chat Sessions
          </h4>
          <button
            type="button"
            onClick={() => setIsSidebarOpen(false)}
            className={[
              "w-8",
              "h-8",
              "rounded-lg!",
              "grid",
              "place-items-center",
              "transition-colors",
              isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-200",
            ].join(" ")}
            aria-label="Close sidebar"
          >
            <Icon
              icon="mdi:chevron-left"
              className={`w-5 h-5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            />
          </button>
        </div>

        {/* Sessions List */}
        <div className="overflow-y-auto overflow-x-hidden min-h-0">
          {chatSessions.length === 0 ? (
            <div className="p-2 md:p-4 text-center">
              <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>No past sessions</p>
            </div>
          ) : (
            <div className="p-2 min-w-0">
              {chatSessions.map((session) => (
                <SessionItem
                  key={session.uid}
                  session={session}
                  isDarkMode={isDarkMode}
                  selectedSessionId={selectedSessionId}
                  setSelectedSessionId={handleSessionSelect}
                  username={username}
                />
              ))}
            </div>
          )}
        </div>

        {/* Sidebar Footer */}
        <div
          className={`p-2 md:p-4 border-t min-w-0 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <button
            type="button"
            onClick={onCreateNewSession}
            className={[
              "w-full",
              "py-2",
              "px-2",
              "md:px-4",
              "rounded-lg!",
              "grid",
              "grid-flow-col",
              "auto-cols-max",
              "gap-2",
              "transition-colors",
              "min-w-0",
              "items-center",
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 text-gray-100!"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700!",
            ].join(" ")}
          >
            <Icon icon="mdi:plus" />
            <span className="font-medium">New Chat</span>
          </button>
        </div>
      </div>
    </aside>
  );
}

export { Sidebar };
